/**
 * Unified HITL (Human-in-the-Loop) Type System
 * 
 * This file defines the contract between backend primitives/workflows
 * and frontend rendering components. All HITL interactions should use
 * these types for consistency and extensibility.
 */

/** 
 * All supported HITL interaction types.
 * Add new types here when extending the system.
 */
export type HITLKind =
  // Basic input types (from hitl.ask_user primitive)
  | 'single_choice'      // Radio buttons - pick one option
  | 'multiple_choice'    // Checkboxes - pick multiple options
  | 'text'               // Single/multi-line text input
  | 'yes_no'             // Simple yes/no decision

  // Rich input types
  | 'rich_text'          // Markdown/rich text editor (email drafts, messages)
  | 'form'               // Editable form with multiple fields (CRM proposals)

  // Preview & Confirmation types
  | 'crm_preview'        // CRM contact preview with planned changes
  | 'confirmation'       // Simple confirm/cancel dialog

  // Special action types
  | 'oauth'              // Triggers OAuth flow (HubSpot, Salesforce, etc.)
  | 'capture'            // Request screenshot/tab capture

/** Option for choice-based HITL interactions */
export interface HITLOption {
  id: string;
  label: string;
  description?: string;   // Additional details shown below label or on expand
  metadata?: Record<string, unknown>;
  isPrimary?: boolean;    // Highlight this option as primary action
  isDestructive?: boolean; // Style as destructive/warning action
}

/** Field schema for form-based HITL */
export interface HITLFormField {
  type: 'text' | 'textarea' | 'email' | 'url' | 'select' | 'number';
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>; // For select type
  format?: string;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  helpText?: string;
  examples?: string[];
}

/** Auto-fix metadata for normalized form inputs */
export interface HITLAutoFix {
  field: string;
  rule: string;
  before: unknown;
  after: unknown;
}

/** Validation error for one form field */
export interface HITLFieldError {
  code: string;
  /** User-facing message (non-technical) */
  message: string;
  suggestedValue?: unknown;
  /** Optional technical details for logs/advanced debug (not for default UI display). */
  technical?: {
    expected?: string;
    received?: unknown;
    details?: string;
  };
}

/** Standardized validation payload for HITL form submissions */
export interface HITLFormValidationPayload {
  code: 'VALIDATION.HITL_FORM_INVALID';
  message: string;
  fieldErrors: Record<string, HITLFieldError>;
  normalizedData?: Record<string, unknown>;
  autoFixes?: HITLAutoFix[];
}

/** Submit result returned by a HITL submit handler */
export interface HITLSubmitResult {
  ok: boolean;
  validation?: HITLFormValidationPayload;
}

/** CRM change representation */
export interface HITLCRMChange {
  field: string;
  from?: unknown;
  to?: unknown;
}

export interface HITLCRMAmbiguousMatch {
  id: string;
  fullName?: string;
  email?: string;
  company?: string;
  phone?: string;
  location?: string;
  createdAt?: string;
  score?: number;
  metadata?: Record<string, unknown>;
}

/** CRM contact preview data */
export interface HITLCRMContact {
  fullName?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  emailGuess?: string;
  company?: string;
  title?: string;
  linkedinUrl?: string;
  location?: string;
}

/** CRM plan data */
export interface HITLCRMPlan {
  mode: 'create' | 'update' | 'ambiguous' | 'noop';
  changes?: HITLCRMChange[];
  contactId?: string;
  ambiguousMatches?: HITLCRMAmbiguousMatch[];
}

export interface HITLMetadata {
  scenario?: string;
  irreversible?: boolean;
  actionToken?: string;
  retryCount?: number;
  retryable?: boolean;
  [key: string]: unknown;
}

/**
 * Normalized HITL Response - THE contract between backend and frontend.
 * 
 * The backend should always return this structure when user input is needed.
 * The frontend uses the `kind` field to select the appropriate renderer.
 */
export interface HITLResponse {
  /** The type of HITL interaction - determines which UI to show */
  kind: HITLKind;

  /**
   * Short, action-oriented label describing what this HITL will do.
   * Used by the UI when rendering answered HITL blocks in collapsed readonly mode.
   * Examples: "Ajouter un contact HubSpot", "Planifier un rendez-vous", "Confirmer l'envoi".
   */
  intentLabel?: string;

  /** Main message/question to display to the user */
  message?: string;

  /** Secondary context/explanation */
  context?: string;

  /** Unique identifier for this HITL question (for resume workflow) */
  questionId?: string;

  /** Step that triggered this HITL */
  stepId?: string;

  // ─────────────────────────────────────────────────────────────
  // Choice-based fields (single_choice, multiple_choice, yes_no)
  // ─────────────────────────────────────────────────────────────
  
  /** Options for choice-based interactions */
  options?: HITLOption[];

  // ─────────────────────────────────────────────────────────────
  // Text input fields (text, rich_text)
  // ─────────────────────────────────────────────────────────────
  
  /** Default value for text inputs */
  defaultValue?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Whether multiline input is expected */
  multiline?: boolean;

  // ─────────────────────────────────────────────────────────────
  // Form fields (form)
  // ─────────────────────────────────────────────────────────────
  
  /** Current form data values */
  formData?: Record<string, unknown>;

  /** Schema defining form fields */
  formSchema?: Record<string, HITLFormField>;

  /** Title for the form/proposal */
  formTitle?: string;

  /** Description for the form/proposal */
  formDescription?: string;

  /** Form validation state + server-side normalization feedback */
  formValidation?: HITLFormValidationPayload;

  // ─────────────────────────────────────────────────────────────
  // CRM Preview fields (crm_preview)
  // ─────────────────────────────────────────────────────────────
  
  /** Contact data for CRM preview */
  contact?: HITLCRMContact;

  /** CRM operation plan */
  plan?: HITLCRMPlan;

  /** URL context (e.g., LinkedIn profile URL) */
  url?: string;

  // ─────────────────────────────────────────────────────────────
  // OAuth fields (oauth)
  // ─────────────────────────────────────────────────────────────
  
  /** OAuth provider to connect (e.g., 'hubspot', 'google_workspace', 'google_calendar', 'gmail') */
  provider?: string;

  /** Additional machine-readable metadata used by orchestrators and advanced UI. */
  metadata?: HITLMetadata;
}

/**
 * Infer HITL kind from a legacy AgentAssistantResult response.
 * This function bridges old-style responses to the new unified system.
 * 
 * @param response - Legacy response object with proposal, review, options, etc.
 * @returns The inferred HITLKind
 */
export const inferHITLKind = (response: Record<string, unknown>): HITLKind | null => {
  // Already has kind - use it
  if (response.kind && typeof response.kind === 'string') {
    return response.kind as HITLKind;
  }

  // Check for hitl field (new normalized format)
  const hitl = response.hitl as HITLResponse | undefined;
  if (hitl?.kind) {
    return hitl.kind;
  }

  // Normalize options first to handle both string[] and HITLOption[]
  const rawOptions = response.options;
  const options = Array.isArray(rawOptions) 
    ? rawOptions.map((o, i) => typeof o === 'string' ? { id: `option_${i}`, label: o } : o as HITLOption)
    : undefined;

  // OAuth detection - options with oauth action
  if (options?.some(o => o.metadata?.action === 'oauth_hubspot' || o.metadata?.action?.toString().startsWith('oauth_'))) {
    return 'oauth';
  }

  // Capture detection
  if (options?.some(o => o.id === 'capture_now' || o.id === 'capture_tab')) {
    return 'capture';
  }

  // Form/Proposal detection
  const proposal = response.proposal as Record<string, unknown> | undefined;
  if (proposal && typeof proposal === 'object') {
    return 'form';
  }

  // CRM Preview detection (review without needsUserInput being the main action)
  const review = response.review as Record<string, unknown> | undefined;
  const needsUserInput = response.needsUserInput as boolean | undefined;
  if (review && !proposal && !options) {
    return 'crm_preview';
  }

  // Simple choice detection
  if (options && options.length > 0) {
    // Yes/No detection
    if (options.length === 2) {
      const ids = options.map(o => o.id.toLowerCase());
      if ((ids.includes('yes') && ids.includes('no')) || 
          (ids.includes('confirm') && ids.includes('cancel'))) {
        return 'yes_no';
      }
    }
    return 'single_choice';
  }

  // Text input detection (kind from ask_user)
  if (response.kind === 'text') {
    return 'text';
  }

  // If needsUserInput but nothing specific, it's a confirmation
  if (needsUserInput) {
    return 'confirmation';
  }

  // Not a HITL response
  return null;
};

/**
 * Normalize options array - handles both string[] and HITLOption[] formats
 */
const normalizeOptions = (rawOptions: unknown): HITLOption[] | undefined => {
  if (!rawOptions || !Array.isArray(rawOptions)) return undefined;
  if (rawOptions.length === 0) return undefined;

  return rawOptions.map((opt, index) => {
    // Already a proper HITLOption object
    if (typeof opt === 'object' && opt !== null && 'id' in opt) {
      return opt as HITLOption;
    }
    
    // String option - convert to HITLOption
    if (typeof opt === 'string') {
      return {
        id: `option_${index}`,
        label: opt,
      };
    }

    // Unknown format - try to extract label
    if (typeof opt === 'object' && opt !== null) {
      const obj = opt as Record<string, unknown>;
      return {
        id: (obj.id as string) || `option_${index}`,
        label: (obj.label as string) || (obj.text as string) || (obj.value as string) || `Option ${index + 1}`,
        description: obj.description as string | undefined,
        metadata: obj.metadata as Record<string, unknown> | undefined,
        isPrimary: obj.isPrimary as boolean | undefined,
      };
    }

    // Fallback
    return {
      id: `option_${index}`,
      label: String(opt),
    };
  });
};

/**
 * Normalize a legacy response into a HITLResponse.
 * This allows gradual migration from old format to new.
 */
export const normalizeHITLResponse = (response: Record<string, unknown>): HITLResponse | null => {
  // Already normalized
  const existingHitl = response.hitl as HITLResponse | undefined;
  if (existingHitl?.kind) {
    // Still normalize options in case they're strings
    if (existingHitl.options) {
      existingHitl.options = normalizeOptions(existingHitl.options);
    }
    return existingHitl;
  }

  const kind = inferHITLKind(response);
  if (!kind) return null;

  const options = normalizeOptions(response.options);
  const proposal = response.proposal as Record<string, unknown> | undefined;
  const review = response.review as Record<string, unknown> | undefined;

  const normalized: HITLResponse = {
    kind,
    message: (response.question || response.answer) as string | undefined,
    questionId: response.questionId as string | undefined,
    options,
  };

  // Enrich based on kind
  if (kind === 'form' && proposal) {
    normalized.formTitle = proposal.title as string | undefined;
    normalized.formDescription = proposal.description as string | undefined;
    normalized.formData = proposal.data as Record<string, unknown> | undefined;
  }

  if ((kind === 'crm_preview' || kind === 'form') && review) {
    normalized.contact = review.contact as HITLCRMContact | undefined;
    normalized.plan = review.plan as HITLCRMPlan | undefined;
    normalized.url = review.url as string | undefined;
  }

  if (kind === 'oauth') {
    const oauthOption = options?.find(o => o.metadata?.action?.toString().startsWith('oauth_'));
    if (oauthOption?.metadata?.action) {
      normalized.provider = String(oauthOption.metadata.action).replace('oauth_', '');
    }
  }

  return normalized;
};

export const isHITLSubmitResult = (value: unknown): value is HITLSubmitResult => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return typeof record.ok === 'boolean';
};
